import React, { useState } from 'react';
import { Plus, Edit, Trash2, Search, TrendingDown, Filter, AlertCircle, CheckCircle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { ExpenseType, CreateExpenseTypeData, UpdateExpenseTypeData } from '../../lib/sdk/index';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { useAllExpenseTypes, useCreateExpenseType, useUpdateExpenseType, useDeleteExpenseType } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

const ExpenseTypes: React.FC = () => {
  // Use React Query hooks
  const { data: expenseTypesData = [], isLoading: loading, refetch: refetchExpenseTypes } = useAllExpenseTypes();
  const expenseTypes = Array.isArray(expenseTypesData) ? expenseTypesData : [];
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingType, setEditingType] = useState<ExpenseType | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); 
  const [deletingType, setDeletingType] = useState<ExpenseType | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<CreateExpenseTypeData>({
    name: ''
  });

  // Form errors
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  // Mutations
  const createExpenseTypeMutation = useCreateExpenseType({
    onSuccess: () => {
      toast.success('Expense type created successfully!');
      setShowAddModal(false);
      setFormData({ name: '' });
      setFormErrors({});
      refetchExpenseTypes();
    },
    onError: (error: any) => {
      console.error('Error creating expense type:', error);
      toast.error(getErrorMessage(error, 'Failed to create expense type'));
    }
  });

  const updateExpenseTypeMutation = useUpdateExpenseType({
    onSuccess: () => {
      toast.success('Expense type updated successfully!');
      setShowEditModal(false);
      setEditingType(null);
      setFormData({ name: '' });
      setFormErrors({});
      refetchExpenseTypes();
    },
    onError: (error: any) => {
      console.error('Error updating expense type:', error);
      toast.error(getErrorMessage(error, 'Failed to update expense type'));
    }
  });

  const deleteExpenseTypeMutation = useDeleteExpenseType({
    onSuccess: () => {
      toast.success('Expense type deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingType(null);
      refetchExpenseTypes();
    },
    onError: (error: any) => {
      console.error('Error deleting expense type:', error);
      toast.error(getErrorMessage(error, 'Failed to delete expense type'));
      setShowDeleteConfirm(false);
      setDeletingType(null);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors: {[key: string]: string} = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (showEditModal && editingType) {
      updateExpenseTypeMutation.mutate({
        id: editingType._id,
        data: formData
      });
    } else {
      createExpenseTypeMutation.mutate(formData);
    }
  };

  const handleEdit = (type: ExpenseType) => {
    setEditingType(type);
    setFormData({ name: type.name });
    setShowEditModal(true);
  };

  const handleDelete = async (type: ExpenseType) => {
    setDeletingType(type);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deletingType) return;
    deleteExpenseTypeMutation.mutate(deletingType._id);
  };

  const handleToggleStatus = async (type: ExpenseType) => {
    try {
      const { ExpenseTypesAPI } = await import('../../lib/sdk/index');
      await ExpenseTypesAPI.toggleExpenseTypeStatus(type._id);
      toast.success(`Expense type ${type.isActive ? 'deactivated' : 'activated'} successfully`);
      refetchExpenseTypes();
    } catch (error: any) {
      console.error('Failed to toggle expense type status:', error);
      toast.error(getErrorMessage(error, 'Failed to update expense type status'));
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingType(null);
    setFormData({ name: '' });
    setFormErrors({});
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeletingType(null);
  };

  const filteredTypes = expenseTypes.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && type.isActive) ||
      (statusFilter === 'inactive' && !type.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Expense Types</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage organizational expense categories for Central Office</p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
        >
          Add Expense Type
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <TrendingDown className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{expenseTypes.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {expenseTypes.filter(type => type.isActive).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <X className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {expenseTypes.filter(type => !type.isActive).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search expense types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Status:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="form-select"
            >
              <option value="all">All Types</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            
            {(statusFilter !== 'all' || searchTerm) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setSearchTerm('');
                }}
                className="ml-2"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Expense Types Table */}
      <div className="card">
        {filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <TrendingDown className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {expenseTypes.length === 0 ? 'No expense types found' : 'No types match your filters'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {expenseTypes.length === 0 
                ? 'Get started by adding your first expense type.'
                : searchTerm 
                  ? 'Try adjusting your search criteria or status filter.'
                  : 'Try adjusting your status filter to see more types.'
              }
            </p>
          </div>
        ) : (
          <TableRoot>
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Status</TH>
                  <TH>Created At</TH>
                  <TH>Updated At</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {filteredTypes.map((type) => (
                  <TR key={type._id} className={!type.isActive ? 'bg-gray-50 dark:bg-gray-800/50' : ''}>
                    <TD className="font-medium">{type.name}</TD>
                    <TD>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        type.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {type.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TD>
                    <TD className="text-sm text-gray-500">{formatDate(type.createdAt)}</TD>
                    <TD className="text-sm text-gray-500">{formatDate(type.updatedAt)}</TD>
                    <TD>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(type)}
                          leftIcon={<Edit className="h-4 w-4" />}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(type)}
                          leftIcon={type.isActive ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          className={type.isActive ? 'text-red-600 hover:text-red-700 ' : 'text-green-600 hover:text-green-700'}
                        >
                          {type.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(type)}
                          leftIcon={<Trash2 className="h-4 w-4" />}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {showAddModal ? 'Add Expense Type' : 'Edit Expense Type'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter expense type name"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && (
                  <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>
                )}
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createExpenseTypeMutation.isLoading || updateExpenseTypeMutation.isLoading}
                  className="flex-1"
                >
                  {(createExpenseTypeMutation.isLoading || updateExpenseTypeMutation.isLoading) ? 'Saving...' : (showAddModal ? 'Add Type' : 'Update Type')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete Expense Type
              </h2>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete "{deletingType.name}"? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={closeDeleteConfirm}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
                className="flex-1"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseTypes;
